// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import { SIDEBAR_WIDTH_DEFAULT, SIDEBAR_WIDTH_WIDE, SIDEBAR_BREAKPOINT, MOBILE_BREAKPOINT } from '../constants';
import type { SidebarSelection, Course } from '../types';
import { useTourEditData } from '../hooks/useTourEditData';
import { useUser } from '@/app/user-context';
import { useCurrentPlayerId, useRecentCourses } from '@/hooks/useRecentCourses';
import { todayLocalDate } from '@/services/localDate';
import { addOneDay } from '../utils';

import Sidebar from './Sidebar';
import SidebarDropdown from './SidebarDropdown';
import EditorPanel from './EditorPanel';
import ConfirmDialog from './shared/ConfirmDialog';

type Props = {
  tourId: string;
};

export default function TourEditLayout({ tourId }: Props) {
  const router = useRouter();
  const { width: windowWidth } = useWindowDimensions();
  const sidebarWidth = windowWidth >= SIDEBAR_BREAKPOINT ? SIDEBAR_WIDTH_WIDE : SIDEBAR_WIDTH_DEFAULT;
  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const data = useTourEditData(tourId);

  const { user } = useUser();
  const currentPlayerId = useCurrentPlayerId(user?.id, user?.token);
  const recentCourses = useRecentCourses(currentPlayerId, user?.token);

  const [selection, setSelection] = useState<SidebarSelection>({ kind: 'tour-name' });
  const [expandedRoundId, setExpandedRoundId] = useState<string | null>(null);
  const [deleteRoundConfirm, setDeleteRoundConfirm] = useState<string | null>(null);

  const playerCount = data.playerRows.filter((r) => r.kind === 'existing').length;
  const gameCount = data.tournamentGamesState.gameIds.length;

  // Single rounds are hidden tours and have no tour-level editing; send them to the
  // round's summary (or the tour list) instead of the tour edit screen.
  useEffect(() => {
    if (!data.isHidden) return;
    const firstRoundId = data.orderedRounds.find((round) => !!round.id)?.id;
    router.replace(firstRoundId ? `/round/${firstRoundId}/summary` : '/my-tours');
  }, [data.isHidden, data.orderedRounds, router]);

  const handleSelect = useCallback((sel: SidebarSelection) => {
    setSelection(sel);
  }, []);

  const handleExpandRound = useCallback((roundId: string | null) => {
    setExpandedRoundId(roundId);
  }, []);

  // --- Instant round creation ---
  const handleAddRound = useCallback(() => {
    const lastRound = data.orderedRounds[data.orderedRounds.length - 1];
    const course = lastRound
      ? data.courses.find((c) => c.name === lastRound.courseName) ?? data.courses[0]
      : data.courses[0];
    if (!course) {
      data.setError('No course available. Please wait for courses to load.');
      return;
    }
    const lastDate = lastRound?.playDate ?? todayLocalDate();
    const nextDate = addOneDay(lastDate);
    const holesCount = lastRound?.holesCount ?? 18;
    const tourPlayers = data.playerRows
      .filter((r) => r.kind === 'existing')
      .map((r) => ({ id: (r as any).playerId }));

    const optimisticId = data.sendCreateRound(course, tourPlayers, nextDate, undefined, holesCount);
    if (optimisticId) {
      optimisticRoundIdRef.current = optimisticId;
      setSelection({ kind: 'round-settings', roundId: optimisticId });
      setExpandedRoundId(optimisticId);
    }

    // Ensure courses are loaded for the settings editor
    if (data.courses.length === 0) {
      data.fetchCourses({ lat: 57.7089, lon: 11.9746 });
    }
  }, [data]);

  // When the optimistic round is replaced by the real one, update all references
  const optimisticRoundIdRef = useRef<string | null>(null);
  useEffect(() => {
    const optimisticId = optimisticRoundIdRef.current;
    if (!optimisticId) return;
    const stillExists = data.orderedRounds.some((r) => r.id === optimisticId);
    if (!stillExists && data.orderedRounds.length > 0) {
      const realRound = data.orderedRounds[data.orderedRounds.length - 1];
      optimisticRoundIdRef.current = null;
      // Update selection if it pointed at the optimistic round
      if ('roundId' in selection && selection.roundId === optimisticId) {
        setSelection({ kind: selection.kind as any, roundId: realRound.id });
        setExpandedRoundId(realRound.id);
      }
      // Update delete confirm dialog if it was targeting the optimistic round
      if (deleteRoundConfirm === optimisticId) {
        setDeleteRoundConfirm(realRound.id);
      }
    }
  }, [data.orderedRounds, selection, deleteRoundConfirm]);

  const handleDeleteRound = useCallback(
    (roundId: string) => {
      setDeleteRoundConfirm(roundId);
    },
    []
  );

  const handleConfirmDeleteRound = useCallback(() => {
    if (!deleteRoundConfirm) return;
    data.sendRoundRemove(deleteRoundConfirm);
    setDeleteRoundConfirm(null);
    // Navigation is handled by the safety effect when the round disappears from orderedRounds
  }, [deleteRoundConfirm, data]);

  // --- Step navigation ---
  const allSteps = useMemo((): SidebarSelection[] => {
    const steps: SidebarSelection[] = [
      { kind: 'tour-name' },
      { kind: 'tour-players' },
      { kind: 'tour-games' },
    ];
    data.orderedRounds.forEach((round) => {
      steps.push({ kind: 'round-settings', roundId: round.id });
      steps.push({ kind: 'round-players', roundId: round.id });
      steps.push({ kind: 'round-games', roundId: round.id });
    });
    return steps;
  }, [data.orderedRounds]);

  const currentStepIndex = useMemo(() => {
    return allSteps.findIndex((step) => {
      if (step.kind !== selection.kind) return false;
      if ('roundId' in step && 'roundId' in selection) return step.roundId === selection.roundId;
      return !('roundId' in step) && !('roundId' in selection);
    });
  }, [allSteps, selection]);

  const isLastStep = currentStepIndex >= allSteps.length - 1;

  // Safety: if selection references a round that no longer exists, navigate away
  useEffect(() => {
    if (!('roundId' in selection)) return;
    const exists = data.orderedRounds.some((r) => r.id === selection.roundId);
    if (!exists) {
      if (data.orderedRounds.length > 0) {
        const fallback = data.orderedRounds[data.orderedRounds.length - 1];
        setSelection({ kind: 'round-settings', roundId: fallback.id });
        setExpandedRoundId(fallback.id);
      } else {
        setSelection({ kind: 'tour-name' });
      }
    }
  }, [data.orderedRounds, selection]);

  const handleNext = useCallback(() => {
    if (isLastStep) return;
    const nextStep = allSteps[currentStepIndex + 1];
    if (nextStep) {
      setSelection(nextStep);
      if ('roundId' in nextStep) {
        setExpandedRoundId(nextStep.roundId);
      }
    }
  }, [allSteps, currentStepIndex, isLastStep]);

  const handleSelectStep = useCallback((step: SidebarSelection) => {
    setSelection(step);
    if ('roundId' in step) {
      setExpandedRoundId(step.roundId);
    }
  }, []);

  const handleUpdateRoundSettings = useCallback(
    (roundId: string, updates: { name?: string; playDate?: string; courseName?: string; course?: Course; holesCount?: number }) => {
      data.updateRoundSettings(roundId, updates);
    },
    [data]
  );

  const handleGoToTours = useCallback(() => {
    router.push('/my-tours');
  }, [router]);

  const deletingRoundName = useMemo(() => {
    if (!deleteRoundConfirm) return '';
    const round = data.orderedRounds.find((r) => r.id === deleteRoundConfirm);
    return round?.name || `Round`;
  }, [deleteRoundConfirm, data.orderedRounds]);

  if (data.loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={appColors.accent.primary} />
        <Text style={styles.loadingText}>Loading tour...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Breadcrumb */}
      <View style={styles.breadcrumb}>
        <Pressable onPress={() => router.back()}>
          <Text style={styles.breadcrumbLink}>← Tours</Text>
        </Pressable>
        <Text style={styles.breadcrumbSep}>/</Text>
        <Text style={styles.breadcrumbCurrent} numberOfLines={1}>
          {data.tourName || 'Tour'}
        </Text>
        <Text style={styles.breadcrumbSep}>/</Text>
        <Text style={styles.breadcrumbCurrent}>Edit</Text>
      </View>

      {/* Main content */}
      <View style={[styles.main, isMobile && styles.mainColumn]}>
        {isMobile ? (
          <SidebarDropdown
            tourName={data.tourName}
            playerCount={playerCount}
            gameCount={gameCount}
            rounds={data.orderedRounds}
            selection={selection}
            expandedRoundId={expandedRoundId}
            onSelect={handleSelect}
            onExpandRound={handleExpandRound}
            onAddRound={handleAddRound}
            onDeleteRound={handleDeleteRound}
          />
        ) : (
          <View style={[styles.sidebar, { width: sidebarWidth }]}>
            <Sidebar
              tourName={data.tourName}
              playerCount={playerCount}
              gameCount={gameCount}
              rounds={data.orderedRounds}
              selection={selection}
              expandedRoundId={expandedRoundId}
              onSelect={handleSelect}
              onExpandRound={handleExpandRound}
              onAddRound={handleAddRound}
              onDeleteRound={handleDeleteRound}
            />
          </View>
        )}
        <View style={styles.editorPanel}>
          <EditorPanel
            selection={selection}
            allSteps={allSteps}
            tourName={data.tourName}
            onTourNameChange={data.setTourName}
            onSendTourUpdate={data.sendTourUpdate}
            saving={data.saving}
            error={data.error}
            statusNote={data.statusNote}
            playerRows={data.playerRows}
            availablePlayers={data.availablePlayers}
            playerRowOpen={data.playerRowOpen}
            onSetPlayerRows={data.setPlayerRows}
            onSetPlayerRowOpen={data.setPlayerRowOpen}
            onSendTourMemberAdd={data.sendTourMemberAdd}
            onSendTourMemberRemove={data.sendTourMemberRemove}
            onSendTemporaryPlayerCreate={data.sendTemporaryPlayerCreate}
            enabledGameDefinitions={data.enabledGameDefinitions}
            tournamentGameIds={data.tournamentGamesState.gameIds}
            tournamentWeightsByGameId={data.tournamentGamesState.weightsByGameId}
            onUpdateTournamentGames={data.updateTournamentGames}
            onUpdateTournamentGameWeight={data.updateTournamentGameWeight}
            rounds={data.orderedRounds}
            courses={data.courses}
            recentCourses={recentCourses}
            loadingCourses={data.loadingCourses}
            courseError={data.courseError}
            onFetchCourses={data.fetchCourses}
            tourMemberOptions={data.tourMemberOptions}
            roundPlayerIdsByRoundId={data.roundPlayerIdsByRoundId}
            playerIdsWithScoresByRoundId={data.playerIdsWithScoresByRoundId}
            playerNameById={data.playerNameById}
            onSendRoundMemberAdd={data.sendRoundMemberAdd}
            onSendRoundMemberRemove={data.sendRoundMemberRemove}
            onSendRoundGroupsSwap={data.sendRoundGroupsSwap}
            roundGroupsByRoundId={data.roundGroupsByRoundId}
            onUpdateRoundSettings={handleUpdateRoundSettings}
            roundGamesByRoundId={data.roundGamesByRoundId}
            onUpdateRoundGames={data.updateRoundGames}
            onUpdateRoundGameWeight={data.updateRoundGameWeight}
            isLastStep={isLastStep}
            onNext={handleNext}
            onGoToTours={handleGoToTours}
            onAddRound={handleAddRound}
            onSelectStep={handleSelectStep}
            isMobile={isMobile}
          />
        </View>
      </View>

      <ConfirmDialog
        visible={deleteRoundConfirm !== null}
        title="Delete Round"
        message={`Are you sure you want to delete "${deletingRoundName}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteRound}
        onCancel={() => setDeleteRoundConfirm(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    backgroundColor: appColors.background.app,
  },
  loadingText: {
    fontSize: 14,
    color: appColors.text.muted,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: appColors.surface.base,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
    gap: 6,
  },
  breadcrumbLink: {
    fontSize: 13,
    fontWeight: '600',
    color: appColors.accent.primary,
  },
  breadcrumbSep: {
    fontSize: 13,
    color: appColors.text.muted,
  },
  breadcrumbCurrent: {
    fontSize: 13,
    fontWeight: '600',
    color: appColors.text.secondary,
    maxWidth: 200,
  },
  main: {
    flex: 1,
    flexDirection: 'row',
  },
  mainColumn: {
    flexDirection: 'column',
  },
  sidebar: {
    flexShrink: 0,
  },
  editorPanel: {
    flex: 1,
  },
});
